// titration.js
import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Kalkulator Titrasi Kimia - Hitung Konsentrasi dan Volume dalam Titrasi";
const DESCRIPTION =
  "Kalkulator titrasi kimia gratis - hitung konsentrasi atau volume larutan dalam proses titrasi secara akurat. Mendukung titrasi asam-basa dan perhitungan molaritas. Alat akurat untuk laboratorium dan pelajar.";
const KEYWORDS =
  "kalkulator titrasi, hitung konsentrasi, hitung volume, titrasi asam basa, rumus titrasi, molaritas, titrasi kimia, titration calculator, molarity calculator";
const OG_URL = "https://do-calculate.com/calculator/id/titration/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Kalkulator Titrasi Kimia",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "EducationalApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Beranda",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Kalkulator Kimia",
        item: "https://do-calculate.com/id/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Kalkulator Titrasi",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Apa rumus titrasi yang digunakan?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Kalkulator menggunakan rumus titrasi dasar: (M1 × V1) / n1 = (M2 × V2) / n2, di mana M adalah konsentrasi molar, V adalah volume, dan n adalah jumlah mol dalam persamaan setara.",
        },
      },
      {
        "@type": "Question",
        name: "Mengapa kita memerlukan jumlah mol (n) dalam perhitungan?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Jumlah mol diperlukan untuk menyeimbangkan persamaan kimia, terutama saat menitrasi asam dan basa di-, tri-, atau poliprotik untuk memastikan keakuratan hasil.",
        },
      },
    ],
  },
];

function TitrationCalculator() {
  const [solveFor, setSolveFor] = useState("M2");
  const [m1, setM1] = useState("");
  const [v1, setV1] = useState("");
  const [n1, setN1] = useState("1");
  const [m2, setM2] = useState("");
  const [v2, setV2] = useState("");
  const [n2, setN2] = useState("1");

  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateTitration = () => {
    try {
      setError("");
      const M1 = parseFloat(m1);
      const V1 = parseFloat(v1);
      const N1 = parseFloat(n1);
      const M2 = parseFloat(m2);
      const V2 = parseFloat(v2);
      const N2 = parseFloat(n2);

      if (N1 <= 0 || N2 <= 0) {
        setError("Jumlah mol (n) harus lebih besar dari nol");
        setResult(null);
        return;
      }

      let finalValue = 0;
      let label = "";
      let unit = "";

      switch (solveFor) {
        case "M2":
          if (!M1 || !V1 || !V2) return;
          finalValue = (M1 * V1 * N2) / (V2 * N1);
          label = "Konsentrasi zat yang tidak diketahui (M2)";
          unit = "M (mol/L)";
          break;
        case "V2":
          if (!M1 || !V1 || !M2) return;
          finalValue = (M1 * V1 * N2) / (M2 * N1);
          label = "Volume zat yang tidak diketahui (V2)";
          unit = "mL";
          break;
        case "M1":
          if (!M2 || !V2 || !V1) return;
          finalValue = (M2 * V2 * N1) / (V1 * N2);
          label = "Konsentrasi larutan standar (M1)";
          unit = "M (mol/L)";
          break;
        case "V1":
          if (!M2 || !V2 || !M1) return;
          finalValue = (M2 * V2 * N1) / (M1 * N2);
          label = "Volume larutan standar (V1)";
          unit = "mL";
          break;
        default:
          break;
      }

      if (isNaN(finalValue) || !isFinite(finalValue)) {
        setResult(null);
      } else {
        setResult({
          value: finalValue,
          label,
          unit,
          formula: `(${M1 || 'M1'} × ${V1 || 'V1'} × ${N2 || 'n2'}) / (${N1 || 'n1'} × ${V2 || 'V2'})`
        });
      }
    } catch (err) {
      setError("Kesalahan dalam perhitungan, harap periksa nilai yang dimasukkan");
      setResult(null);
    }
  };

  useEffect(() => {
    calculateTitration();
  }, [solveFor, m1, v1, n1, m2, v2, n2]);

  const handleClear = () => {
    setM1("");
    setV1("");
    setN1("1");
    setM2("");
    setV2("");
    setN2("1");
    setResult(null);
    setError("");
  };

  return (
    <div className="ltr" >
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>Kalkulator Titrasi</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Kalkulator Titrasi</strong> adalah alat laboratorium akurat yang membantu kimiawan dan pelajar dalam <strong>menghitung konsentrasi molar</strong> atau <strong>volume</strong> yang diperlukan dalam reaksi penetralan. Kalkulator ini didasarkan pada rumus titrasi molar untuk memastikan hasil yang benar ketika menangani asam dan basa dengan berbagai kekuatan dan jumlah proton.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  Lakukan Perhitungan Titrasi
                </h2>
              </div>

              <div className="row mb-3">
                <div className="col-sm-12">
                  <label >Variabel yang Akan Dihitung</label>
                  <select
                    className="form-select"
                    value={solveFor}
                    onChange={(e) => {
                      setSolveFor(e.target.value);
                      handleClear();
                    }}
                  >
                    <option value="M2">Konsentrasi zat yang tidak diketahui (M2)</option>
                    <option value="V2">Volume zat yang tidak diketahui (V2)</option>
                    <option value="M1">Konsentrasi larutan standar (M1)</option>
                    <option value="V1">Volume larutan standar (V1)</option>
                  </select>
                </div>
              </div>

              <div className="row">
                <div className="col-12"><h5 className="text-secondary border-bottom pb-2">Larutan Standar (Diketahui)</h5></div>
                <div className="col-sm-12 col-md-4">
                  <label >Konsentrasi M1 (mol/L)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={m1}
                    disabled={solveFor === "M1"}
                    onChange={(e) => setM1(e.target.value)}
                    placeholder="M1"
                  />
                </div>
                <div className="col-sm-12 col-md-4">
                  <label >Volume V1 (mL)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={v1}
                    disabled={solveFor === "V1"}
                    onChange={(e) => setV1(e.target.value)}
                    placeholder="V1"
                  />
                </div>
                <div className="col-sm-12 col-md-4">
                  <label >Jumlah mol n1</label>
                  <input
                    type="number"
                    className="form-control"
                    value={n1}
                    onChange={(e) => setN1(e.target.value)}
                    placeholder="n1"
                  />
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-12"><h5 className="text-secondary border-bottom pb-2">Larutan yang Tidak Diketahui (Analit)</h5></div>
                <div className="col-sm-12 col-md-4">
                  <label >Konsentrasi M2 (mol/L)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={m2}
                    disabled={solveFor === "M2"}
                    onChange={(e) => setM2(e.target.value)}
                    placeholder="M2"
                  />
                </div>
                <div className="col-sm-12 col-md-4">
                  <label >Volume V2 (mL)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={v2}
                    disabled={solveFor === "V2"}
                    onChange={(e) => setV2(e.target.value)}
                    placeholder="V2"
                  />
                </div>
                <div className="col-sm-12 col-md-4">
                  <label >Jumlah mol n2</label>
                  <input
                    type="number"
                    className="form-control"
                    value={n2}
                    onChange={(e) => setN2(e.target.value)}
                    placeholder="n2"
                  />
                </div>
              </div>

              <div className="text-center mb-3 mt-4">
                <button onClick={handleClear} className="btn btn-secondary" style={{ padding: "10px 30px" }}>
                  Hapus Data
                </button>
              </div>

              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>Peringatan:</strong> {error}
                </div>
              )}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>Hasil:</strong>
                  </h3>
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Variabel</th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Nilai yang Diekstrak</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>{result.label}</td>
                          <td style={{ textAlign: "center", fontSize: "20px", fontWeight: "bold", color: "#1a5f3f" }}>
                            {result.value.toFixed(4)} {result.unit}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>Apa itu Proses Titrasi Kimia?</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Titrasi adalah teknik analisis kimia laboratorium yang digunakan untuk menentukan konsentrasi larutan yang tidak diketahui (disebut analit) dengan mereaksikannya dengan volume yang diketahui dari larutan yang konsentrasinya diketahui secara akurat (disebut larutan standar atau titran).
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Larutan Standar:</strong> adalah larutan yang biasanya kita tempatkan di dalam buret.</li>
                  <li><strong>Titik Ekuivalen:</strong> adalah saat di mana jumlah zat standar yang ditambahkan sama secara kimia dengan jumlah zat yang tidak diketahui.</li>
                  <li><strong>Indikator:</strong> zat yang berubah warna ketika titik akhir titrasi tercapai.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Rumus Perhitungan Titrasi</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Perhitungan pada dasarnya didasarkan pada rasio molar dari persamaan setara:
                </p>
                <div className="alert alert-info text-center" style={{ direction: "ltr" }}>
                  <strong>(M1 × V1) / n1 = (M2 × V2) / n2</strong>
                </div>
                <p>Di mana:</p>
                <ul>
                  <li>M: Molaritas (mol/L).</li>
                  <li>V: Volume (harus menggunakan satuan yang sama untuk kedua sisi).</li>
                  <li>n: Jumlah mol dalam persamaan setara.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Contoh Praktis Perhitungan Titrasi</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px" }}>Contoh 1: Titrasi Asam HCl dengan Basa NaOH</h3>
                <p>Reaksi: HCl + NaOH → NaCl + H2O (rasio 1:1)</p>
                <p>Jika kita memerlukan 25 mL NaOH 0,1M untuk menitrasi 20 mL HCl, berapa konsentrasi asamnya?</p>
                <div className="alert alert-success">
                  Hasil: M2 = (0,1 × 25 × 1) / (20 × 1) = 0,125 M
                </div>

                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>Contoh 2: Asam Dua Proton</h3>
                <p>Reaksi: H2SO4 + 2NaOH → Na2SO4 + 2H2O</p>
                <p>Di sini n asam = 1, dan n basa = 2. Nilai-nilai ini harus dimasukkan dalam kolom jumlah mol untuk memastikan keakuratan perhitungan.</p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Tips Penggunaan yang Benar</strong>
              </div>
              <div className="info-section-body">
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Pastikan untuk menulis persamaan kimia setara sebelum memulai untuk menentukan nilai n1 dan n2.</li>
                  <li>Pastikan untuk membaca volume buret secara akurat pada meniskus cairan.</li>
                  <li>Jika volume dalam mililiter di satu sisi, harus dalam mililiter juga di sisi lain.</li>
                  <li>Gunakan indikator yang sesuai yang berubah warna pada pH yang diharapkan dari titik ekuivalen.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Pertanyaan Umum tentang Titrasi</strong>
              </div>
              <div className="info-section-body">
                <div className="mb-3">
                  <strong>1. Apa perbedaan antara titik ekuivalen dan titik akhir?</strong>
                  <p>Titik ekuivalen adalah titik teoritis di mana mol sama, sedangkan titik akhir adalah saat ketika warna indikator benar-benar berubah dan kita menghentikan penambahan.</p>
                </div>
                <div className="mb-3">
                  <strong>2. Bisakah volume dalam liter digunakan?</strong>
                  <p>Ya, Anda dapat menggunakan liter atau mililiter, asalkan satuannya sama di kedua sisi persamaan untuk mendapatkan hasil yang benar.</p>
                </div>
                <div className="mb-3">
                  <strong>3. Bagaimana cara menangani asam kuat dan basa lemah?</strong>
                  <p>Perhitungan molar tetap sama, tetapi pemilihan indikator kimialah yang berbeda agar sesuai dengan lingkungan pada titik netralisasi.</p>
                </div>
                <div className="mb-3">
                  <strong>4. Apa itu molaritas?</strong>
                  <p>Molaritas adalah ukuran konsentrasi zat terlarut dalam larutan, sama dengan jumlah mol zat terlarut dibagi dengan volume larutan dalam liter.</p>
                </div>
                <div className="mb-3">
                  <strong>5. Mengapa hasil ditampilkan dalam format desimal panjang?</strong>
                  <p>Akurasi penting dalam kimia; kalkulator menampilkan 4 angka desimal untuk memastikan ketelitian analisis laboratorium dan menghindari kesalahan pembulatan yang besar.</p>
                </div>
                <div className="mb-3">
                  <strong>6. Bagaimana cara menghitung jumlah mol (n)?</strong>
                  <p>Nilai-nilai ini diambil langsung dari koefisien di sebelah kiri rumus kimia dalam persamaan kimia setara.</p>
                </div>
                <div className="mb-3">
                  <strong>7. Apa yang harus saya lakukan jika reaksinya bukan asam-basa?</strong>
                  <p>Hukum (MV/n) berlaku untuk semua reaksi titrasi (seperti titrasi redoks) selama Anda memiliki persamaan setara.</p>
                </div>
                <div className="mb-3">
                  <strong>8. Apakah kalkulator mendukung suhu?</strong>
                  <p>Perhitungan didasarkan pada volume dan konsentrasi standar; dalam kondisi laboratorium normal, kami menganggap volume konstan tetapi lebih baik melakukan eksperimen pada suhu kamar.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TitrationCalculator;